import React from 'react';
import Button from '@carvana/core-helios-button';
import { RepairFacility } from '@components/service-and-repair/services/repair-facilities-service';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { datadogRum } from '@datadog/browser-rum';
import { LinkWithNativeEvents, sendNativeMessage, NativeMessageEvent } from '@components/_common/link/link';
import { isCarvanaNativeApp } from '@carvana/core-native-messaging';
import clsx from 'clsx';
import styles from './result-tile.module.css';
import StarFilled from './icons/star-filled_cvna.svg';
import Star from './icons/star.svg';
import GoogleLogo from './icons/Icon R.svg';
import MapPin from './icons/map-pin.svg';
import Phone from './icons/phone.svg';
import '@carvana/core-helios-button/index.css';

export type ResultTileProps = Partial<RepairFacility> & {
  description?: string;
  onClick?: () => void;
  buttonType?: 'appointment' | 'website';
  disclaimer?: string;
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.5 1C9.05228 1 9.5 1.44772 9.5 2V3H15.5V2C15.5 1.44772 15.9477 1 16.5 1C17.0523 1 17.5 1.44772 17.5 2V3H19.5C21.1569 3 22.5 4.34315 22.5 6V20C22.5 21.6569 21.1569 23 19.5 23H5.5C3.84315 23 2.5 21.6569 2.5 20V6C2.5 4.34315 3.84315 3 5.5 3H7.5V2C7.5 1.44772 7.94772 1 8.5 1ZM7.5 5H5.5C4.94772 5 4.5 5.44772 4.5 6V9H20.5V6C20.5 5.44771 20.0523 5 19.5 5H17.5V6C17.5 6.55228 17.0523 7 16.5 7C15.9477 7 15.5 6.55228 15.5 6V5H9.5V6C9.5 6.55228 9.05228 7 8.5 7C7.94772 7 7.5 6.55228 7.5 6V5ZM20.5 11H4.5V20C4.5 20.5523 4.94771 21 5.5 21H19.5C20.0523 21 20.5 20.5523 20.5 20V11Z"
      fill="currentColor"
    />
  </svg>
);

export const formatPhoneNumber = (phoneNumber: string) => {
  const match = phoneNumber.match(/^(\d{3})-?(\d{3})-?(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

const ResultTile = (props: ResultTileProps) => {
  const {
    vendorId,
    name,
    description,
    distanceFromLocation,
    rating,
    street,
    street2,
    city,
    state,
    zip5,
    phoneNumber,
    website,
    url,
    buttonType,
    disclaimer,
  } = props || {};
  const roundedRating = rating && Math.round(parseFloat(rating as any));
  const hasBody = !!street || !!phoneNumber;

  const reviewsData = roundedRating
    ? new Array(5).fill('').map((_, index) =>
        index < roundedRating
          ? {
              src: StarFilled,
              alt: 'star-filled',
            }
          : {
              src: Star,
              alt: 'star',
            }
      )
    : null;

  const trackerUserAction = (eventName: string) => {
    const analyticsProps = {
      vendorId,
      variant: 2,
    };
    Analytics.trackEvent(eventName, analyticsProps);
    datadogRum.addAction(eventName, analyticsProps);
  };

  const AddressWrapper = ({ children }: { children: React.ReactNode }) =>
    url ? (
      <LinkWithNativeEvents
        href={url}
        data-testid="result-tile-address"
        rel="noreferrer"
        target="_blank"
        className={styles['address-link']}
        onClick={() => trackerUserAction(AnalyticEvents.sarCoverageScreenClickedSuggestedPartnerAddress)}
        nativeEvent={{
          event: 'link',
          payload: { url },
        }}
      >
        {children}
      </LinkWithNativeEvents>
    ) : (
      <div className={styles['address-text']}>{children}</div>
    );

  const CTAButton =
    buttonType === 'website'
      ? {
          icon: undefined,
          text: 'Visit Website',
        }
      : {
          icon: <CalendarIcon />,
          text: 'Schedule appointment',
        };

  const analyticsProps = {
    variant: 2,
    name,
    vendorId,
    link: website,
  };
  const handleNavigation = (url: string, analytics: typeof analyticsProps) => {
    if (isCarvanaNativeApp()) {
      sendNativeMessage('link' as NativeMessageEvent, { url });
    } else {
      window.open(url, '_blank');
    }
    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenClickedSuggestedPartner, analytics);
    datadogRum.addAction(AnalyticEvents.sarCoverageScreenClickedSuggestedPartner, analytics);
  };

  return (
    <div className={styles['result-tile']}>
      <div className={styles['content']}>
        <div className={styles['headline']}>
          <div className={styles['heading']}>
            <span className={styles['name']}>{name}</span>
            {distanceFromLocation && <span className={styles['distance']}>{distanceFromLocation} mi</span>}
          </div>
          {description && <span className={styles['description']}>{description}</span>}

          {reviewsData && (
            <div className={styles['reviews']}>
              <div className={styles['stars']}>
                {reviewsData.map(({ src, alt }, _ind) => (
                  <img key={_ind} src={src} alt={alt} />
                ))}
              </div>
              <div className={styles['rating']}>
                {rating} <img src={GoogleLogo} className={styles['google-logo']} alt="" />
              </div>
            </div>
          )}
        </div>

        {hasBody && (
          <div className={styles['body']}>
            {street && (
              <div className={styles['address']}>
                <div className={styles['street']}>
                  <img src={MapPin} alt="address" className={styles['address-pin']} />
                  <AddressWrapper>
                    <>
                      {street} {street2}
                      <br />
                      {city} {state}, {zip5}
                    </>
                  </AddressWrapper>
                </div>
              </div>
            )}
            {phoneNumber && (
              <div className={styles['phone-number']}>
                <span className={styles['icon']}>
                  <img src={Phone} alt="phone-number" />
                </span>
                <LinkWithNativeEvents
                  href={`tel:${phoneNumber}`}
                  rel="tel"
                  data-testid="result-tile-phonenumber"
                  className={clsx(styles['number'], styles['mobile'])}
                  onClick={() => trackerUserAction(AnalyticEvents.sarCoverageScreenClickedSuggestedPartnerPhoneNumber)}
                  nativeEvent={{
                    event: 'link',
                    payload: { url: `tel:${phoneNumber}` },
                  }}
                >
                  {formatPhoneNumber(phoneNumber)}
                </LinkWithNativeEvents>
                <span className={clsx(styles['number'], styles['desktop'])}>{formatPhoneNumber(phoneNumber)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {website && (
        <div className={styles['ctas']}>
          <Button
            iconLeft={CTAButton.icon}
            priority="secondary"
            variant="default"
            size="medium"
            className={buttonType === 'appointment' ? styles['appointment-button'] : ''}
            onClick={() => handleNavigation(website, analyticsProps)}
          >
            <span>{CTAButton.text}</span>
          </Button>
          {disclaimer && <div className={styles['disclaimer']}>{disclaimer}</div>}
        </div>
      )}
    </div>
  );
};

export default ResultTile;
